import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Sidebar } from './components/sidebar/sidebar';
import { RouterModule } from '@angular/router';
import { Navbar } from './components/navbar/navbar';
import { ContentSyncService } from '../core/services/content-sync.service';
import { SignalRService } from '../shared/services/signal-r.service';
import { ManualApprovalService } from '../features/manual-approval/services/manual-approval.service';
import {
    LIVE_POLLING_INTERVALS,
    LivePollingService,
} from '../core/services/live-polling.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-layout',
    imports: [Sidebar, RouterModule, Navbar],
    templateUrl: './layout.html',
    styleUrl: './layout.scss',
})
export class Layout implements OnInit, OnDestroy {
    private readonly contentSyncService = inject(ContentSyncService);
    private readonly signalRService = inject(SignalRService);
    private readonly manualApprovalService = inject(ManualApprovalService);
    private readonly livePollingService = inject(LivePollingService);

    /**
     * AB#9714: the manual-approval held-queue poller (mergeManualApprovalTickets,
     * which bumps the alert signal for genuinely new tickets per AB#9470)
     * previously ran only on the Manual Approval list and Dashboard screens
     * (manual-approval-list.ts, dashboard.service.ts). On any other screen a
     * ticket held during a SignalR reconnect gap was never merged into the
     * backlog, so the top-bar badge/bell stayed silent until the trader
     * navigated to one of those two screens — and on a fresh login the badge
     * read 0 until the first poll on one of them fired.
     *
     * Hosting the SAME poller here in the always-mounted shell makes the
     * fallback genuinely app-wide: LivePollingService fires an immediate
     * fetch on creation (timer(0, ...)), which is what hydrates the badge to
     * the correct count right after a fresh login, before any live SignalR
     * arrival or per-screen poll. The per-screen pollers are left in place —
     * their lastUpdated/status signals still drive the live-status indicator
     * and the dashboard widget states — and merging is idempotent by id
     * (mergeManualApprovalTickets already de-dupes against the live backlog
     * and decidedManualApprovalIds), so the overlapping fetch never double-
     * counts a ticket or double-sounds the alert (AB#8970).
     */
    private readonly manualApprovalPoller =
        this.livePollingService.createPoller(
            () => this.manualApprovalService.loadRecentPendingAttempts(),
            LIVE_POLLING_INTERVALS.manualApproval,
        );

    ngOnInit(): void {
        this.signalRService.setupConnection();
        // Documented teardown exceptions (AB#8777): the root Layout lives for
        // the whole app session, so these one-shot bootstrap HTTP calls never
        // outlive their component. They complete on their own and need no
        // takeUntilDestroyed guard.
        this.signalRService.initializeBetTickerBacklog(10).subscribe();
        this.contentSyncService.syncOnAppOpen().subscribe();
    }

    ngOnDestroy(): void {
        // Layout sits outside the /auth route tree, so it is destroyed on
        // logout and remounted on the next login (mirrors AuthService.logout()
        // tearing down SignalRService for the same shared-terminal/cross-user
        // concern, AB#8980). Stop the poller here so a stale instance never
        // keeps firing across that boundary.
        this.manualApprovalPoller.stop();
    }
}
